"use client";

import { useCallback } from "react";
import log from "logbeacon";
import { useOpenChatModal } from "@/src/hooks/useOpenChatModal";
import { useChatRecommendQuestion, useShellRouter } from "@/src/hooks";
import { useIsPc } from "@/src/hooks/useIsPc";
import type { IOnCreateParams } from "@/src/hooks/useCreateChat";
import { openChatTabWithPendingEntry } from "@/src/utils/chatUtils/chatTabEntry";

export type OpenResponsiveChatParams = Pick<
  IOnCreateParams,
  | "question"
  | "autoSubmit"
  | "autoRenew"
  | "cacheQuestion"
  | "sign"
  | "tpl"
  | "secondOpinion"
>;

/**
 * 按视口分派 Chat 打开方式：
 * - **大屏（PC）**：`openChatTabWithPendingEntry` → `/chat` 全页
 * - **小屏**：`useOpenChatModal` 弹窗
 */
export function useResponsiveChatOpen() {
  const router = useShellRouter();
  const handleOpenChatModal = useOpenChatModal();
  const [isPC] = useIsPc();
  const { questions: chatQuestion } = useChatRecommendQuestion();

  const openResponsiveChat = useCallback(
    (params?: OpenResponsiveChatParams) => {
      const {
        question,
        autoRenew = true,
        autoSubmit = true,
        ...rest
      } = params || {};
      const chatParams = {
        question,
        autoRenew,
        autoSubmit,
        questions: chatQuestion?.slice(0, 4) || [],
        ...rest,
      };

      if (isPC) {
        log.info("[ResponsiveChat] navigate chat tab with pending entry", {
          autoRenew,
          autoSubmit,
        });
        openChatTabWithPendingEntry(router, chatParams);
        return;
      }

      log.info("[ResponsiveChat] open chat modal", {
        autoRenew,
        autoSubmit,
      });
      void handleOpenChatModal(chatParams);
    },
    [handleOpenChatModal, isPC, router],
  );

  return { openResponsiveChat };
}
